import React, { useEffect, useState } from "react";
import "../../Styles/Seller/SellerSetting.css";
import sellerApi from "../../utils/sellerApi";
import { showError, showSuccess } from "../../utils/Toast";
import {
  FaUser,
  FaStore,
  FaShieldAlt,
  FaBell,
  FaCreditCard,
  FaPalette,
  FaGlobe,
  FaFileAlt,
  FaLock,
  FaUpload,
  FaSave,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaCamera,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaChartLine,
  FaBoxOpen,
  FaShippingFast,
  FaPercent,
  FaRegClock,
  FaLanguage,
  FaChevronRight,
  FaTrash,
  FaLink,
  FaTag,
  FaDollarSign,
} from "react-icons/fa";
import {
  FiSettings,
  FiPackage,
  FiTruck,
  FiEdit,
  FiCopy,
  FiStar,
} from "react-icons/fi";

const UAE_EMIRATES = [
  "Dubai",
  "Abu Dhabi",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

const ADDRESS_PROOF_MAX_FILE_SIZE = 5 * 1024 * 1024;

const createEmptyAddressForm = () => ({
  address_line1: "",
  address_line2: "",
  area: "",
  city: "",
  emirate: "Dubai",
  country: "United Arab Emirates",
  postal_code: "",
});

const buildAddressFormFromVerification = (verification, seller = {}) => {
  const source =
    verification?.pending_address ||
    verification?.latest_rejected_address ||
    verification?.approved_address ||
    null;

  if (source) {
    return {
      address_line1: source.address_line1 || "",
      address_line2: source.address_line2 || "",
      area: source.area || "",
      city: source.city || "",
      emirate: source.emirate || "Dubai",
      country: source.country || "United Arab Emirates",
      postal_code: source.postal_code || "",
    };
  }

  return {
    address_line1: seller?.address || "",
    address_line2: "",
    area: "",
    city: seller?.city || "",
    emirate: seller?.state || "Dubai",
    country: seller?.country || "United Arab Emirates",
    postal_code: seller?.pincode || "",
  };
};

const getAddressVerificationMessage = (verification) => {
  const status = verification?.status || "not_submitted";
  const hasApprovedAddress = Boolean(verification?.approved_address);

  if (status === "pending" && hasApprovedAddress) {
    return "Your updated business address is under verification. Your currently approved address will remain active until the new address is approved.";
  }

  if (status === "pending") {
    return "Your business address is under verification.";
  }

  if (status === "approved") {
    return "Your business address has been verified.";
  }

  if (status === "rejected" && hasApprovedAddress) {
    return "Your latest business address submission was rejected. Your currently approved address remains active until you submit a new address for review.";
  }

  if (status === "rejected") {
    return "Your business address verification was rejected. Please correct the information and submit it again.";
  }

  return "Please add your business address and upload address proof for verification.";
};

const getStatusClassName = (status) => {
  if (status === "approved") return "seller-address-status-badge approved";
  if (status === "pending") return "seller-address-status-badge pending";
  if (status === "rejected") return "seller-address-status-badge rejected";
  return "seller-address-status-badge neutral";
};

function SellerSetting() {
  const [activeTab, setActiveTab] = useState("profile");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
  });
  const [businessAddressForm, setBusinessAddressForm] = useState(
    createEmptyAddressForm()
  );
  const [businessAddressVerification, setBusinessAddressVerification] =
    useState(null);
  const [addressProofFile, setAddressProofFile] = useState(null);
  const [proofInputKey, setProofInputKey] = useState(0);
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [proofPreviewLoading, setProofPreviewLoading] = useState(false);
  const [stats, setStats] = useState({
    activeProducts: 0,
    monthlySales: "0.00",
    pendingOrders: 0,
  });
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    orderUpdates: true,
    promotions: false,
    lowStock: true,
    newReviews: true,
    marketingTips: false,
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [storeData, setStoreData] = useState({
    storeName: "My Awesome Store",
    description: "Premium products with fast delivery across the globe",
    contactEmail: "seller@example.com",
    phone: "+1 (555) 123-4567",
    address: "123 Commerce St, Business City",
    currency: "AED",
    timezone: "GMT+5",
    language: "English",
    storeUrl: "",
  });
  const [paymentMethods, setPaymentMethods] = useState({
    stripe: true,
    paypal: true,
    bankTransfer: false,
    cashOnDelivery: true,
  });

  const menuItems = [
    { id: "profile", label: "Profile", icon: <FaUser /> },
    // { id: "store", label: "Store Settings", icon: <FaStore /> },
    { id: "security", label: "Security", icon: <FaShieldAlt /> },
    { id: "notifications", label: "Notifications", icon: <FaBell /> },
    { id: "payment", label: "Payment", icon: <FaCreditCard /> },
    { id: "shipping", label: "Shipping", icon: <FiTruck /> },
    { id: "appearance", label: "Appearance", icon: <FaPalette /> },
    // { id: "integration", label: "Integrations", icon: <FaLink /> },
    { id: "documents", label: "Documents", icon: <FaFileAlt /> },
  ];

  useEffect(() => {
    const fetchSellerDashboard = async () => {
      try {
        const response = await sellerApi.get("getSellerDashboard");
        const responseData = response.data;
        const sellerData = responseData?.data;
        // console.log('responseData===>', responseData, sellerData)

        if (responseData?.success) {
          setFormData({
            username: sellerData?.seller?.username ?? "",
            email: sellerData?.seller?.email ?? "",
            phone: sellerData?.seller?.phone ?? "",
          });
          setBusinessAddressVerification(
            sellerData?.business_address_verification || null
          );
          setBusinessAddressForm(
            buildAddressFormFromVerification(
              sellerData?.business_address_verification,
              sellerData?.seller || {}
            )
          );

          setStats({
            activeProducts: sellerData?.active_products ?? 0,
            monthlySales: sellerData?.monthly_sales ?? "0.00",
            pendingOrders: sellerData?.pending_orders ?? 0,
          });
        } else {
          showError(responseData?.message || "Failed to load seller dashboard");
        }
      } catch (error) {
        console.error("Failed to fetch seller dashboard:", error);
        showError(
          error.response?.data?.message || "Failed to load seller dashboard"
        );
      }
    };

    fetchSellerDashboard();
  }, []);

  const handleInputChange = (field, value) => {
    setStoreData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNotificationToggle = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePaymentToggle = (method) => {
    setPaymentMethods((prev) => ({ ...prev, [method]: !prev[method] }));
  };

  const handleSaveProfile = async () => {
    try {
      const response = await sellerApi.post("updateUser", {
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
      });
      const data = response.data;

      if (data?.success) {
        showSuccess(data?.message || "Profile updated successfully");
      } else {
        showError(data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Failed to update seller profile:", error);
      showError(error.response?.data?.message || "Something went wrong");
    }
  };

  const handleBusinessAddressChange = (e) => {
    const { name, value } = e.target;

    setBusinessAddressForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProofFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    if (!selectedFile) {
      setAddressProofFile(null);
      return;
    }

    const hasAllowedExtension = /\.(pdf|jpe?g|png)$/i.test(
      selectedFile.name || ""
    );
    const hasAllowedType = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ].includes(String(selectedFile.type || "").toLowerCase());

    if (!hasAllowedExtension || !hasAllowedType) {
      showError("Only PDF, JPG, JPEG, and PNG files are allowed.");
      event.target.value = "";
      setAddressProofFile(null);
      return;
    }

    if (!selectedFile.size) {
      showError("Please select a valid address proof file.");
      event.target.value = "";
      setAddressProofFile(null);
      return;
    }

    if (selectedFile.size > ADDRESS_PROOF_MAX_FILE_SIZE) {
      showError("Address proof must be 5 MB or smaller.");
      event.target.value = "";
      setAddressProofFile(null);
      return;
    }

    setAddressProofFile(selectedFile);
  };

  const openProofPreview = async (verificationId) => {
    if (!verificationId) return;

    try {
      setProofPreviewLoading(true);
      const response = await sellerApi.get(
        `getSellerAddressVerificationProof/${verificationId}`,
        {
          responseType: "blob",
        }
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
      showError(
        error.response?.data?.message || "Failed to load the address proof."
      );
    } finally {
      setProofPreviewLoading(false);
    }
  };

  const handleSubmitBusinessAddress = async () => {
    if (!addressProofFile) {
      showError("Please upload a business address proof document.");
      return;
    }

    try {
      setAddressSubmitting(true);
      const payload = new FormData();

      Object.entries(businessAddressForm).forEach(([key, value]) => {
        payload.append(key, value || "");
      });
      payload.append("proof_file", addressProofFile);

      const response = await sellerApi.post(
        "submitSellerAddressVerification",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const verification = response.data?.business_address_verification || null;
      setBusinessAddressVerification(verification);
      setBusinessAddressForm(buildAddressFormFromVerification(verification));
      setAddressProofFile(null);
      setProofInputKey((prev) => prev + 1);
      showSuccess(
        response.data?.message || "Business address submitted successfully."
      );
    } catch (error) {
      console.error("Failed to submit seller business address:", error);
      showError(
        error.response?.data?.message ||
          "Failed to submit the business address for verification."
      );
    } finally {
      setAddressSubmitting(false);
    }
  };

  const integrations = [
    { name: "Google Analytics", connected: true, icon: "📊" },
    { name: "Facebook Pixel", connected: true, icon: "📱" },
    { name: "Mailchimp", connected: false, icon: "✉️" },
    { name: "QuickBooks", connected: false, icon: "💰" },
    { name: "Shopify", connected: false, icon: "🛍️" },
    { name: "Zapier", connected: true, icon: "⚡" },
  ];

  const renderContent = () => {
    const approvedBusinessAddress =
      businessAddressVerification?.approved_address || null;
    const pendingBusinessAddress =
      businessAddressVerification?.pending_address || null;
    const latestRejectedBusinessAddress =
      businessAddressVerification?.latest_rejected_address || null;
    const verificationStatus =
      businessAddressVerification?.status || "not_submitted";
    const activeProofRecord =
      pendingBusinessAddress ||
      (verificationStatus === "rejected"
        ? latestRejectedBusinessAddress
        : approvedBusinessAddress);

    switch (activeTab) {
      case "profile":
        return (
          <div className="seller-profile-section-white">

            <div className="seller-form-grid-white">
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Full Name</label>
                <input
                  name="username"
                  type="text"
                  className="seller-form-input-white"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="John Doe"
                />
              </div>
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Email Address</label>
                <div className="seller-input-icon-white">
                  <FaEnvelope />
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Phone Number</label>
                <div className="seller-input-icon-white">
                  <FaPhone />
                  <input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="seller-address-verification-panel">
              <div className="seller-address-verification-header">
                <div>
                  <h3>Business Address Verification</h3>
                  <p>
                    Only your approved business address will be used as the
                    seller pickup address for EMX shipments.
                  </p>
                </div>
                <span className={getStatusClassName(verificationStatus)}>
                  {(verificationStatus || "not_submitted")
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (character) => character.toUpperCase())}
                </span>
              </div>

              <div className="seller-address-note-banner">
                {getAddressVerificationMessage(businessAddressVerification)}
              </div>

              {businessAddressVerification?.rejection_reason && (
                <div className="seller-address-rejection-box">
                  <strong>Rejection Reason:</strong>{" "}
                  {businessAddressVerification.rejection_reason}
                </div>
              )}

              <div className="seller-address-summary-grid">
                {approvedBusinessAddress && (
                  <div className="seller-address-summary-card approved">
                    <div className="seller-address-summary-title">
                      Approved Business Address
                    </div>
                    <p>
                      {approvedBusinessAddress.business_address_text ||
                        "Approved address is available."}
                    </p>
                    {approvedBusinessAddress.proof_available && (
                      <button
                        type="button"
                        className="seller-address-proof-link"
                        onClick={() =>
                          openProofPreview(approvedBusinessAddress.id)
                        }
                        disabled={proofPreviewLoading}
                      >
                        View Approved Proof
                      </button>
                    )}
                  </div>
                )}

                {pendingBusinessAddress && (
                  <div className="seller-address-summary-card pending">
                    <div className="seller-address-summary-title">
                      Pending Business Address
                    </div>
                    <p>
                      {pendingBusinessAddress.business_address_text ||
                        "Pending address is available for review."}
                    </p>
                    {pendingBusinessAddress.proof_available && (
                      <button
                        type="button"
                        className="seller-address-proof-link"
                        onClick={() =>
                          openProofPreview(pendingBusinessAddress.id)
                        }
                        disabled={proofPreviewLoading}
                      >
                        View Pending Proof
                      </button>
                    )}
                  </div>
                )}

                {verificationStatus === "rejected" &&
                  latestRejectedBusinessAddress && (
                    <div className="seller-address-summary-card rejected">
                      <div className="seller-address-summary-title">
                        Latest Rejected Submission
                      </div>
                      <p>
                        {latestRejectedBusinessAddress.business_address_text ||
                          "Your latest business address submission was rejected."}
                      </p>
                      {latestRejectedBusinessAddress.proof_available && (
                        <button
                          type="button"
                          className="seller-address-proof-link"
                          onClick={() =>
                            openProofPreview(latestRejectedBusinessAddress.id)
                          }
                          disabled={proofPreviewLoading}
                        >
                          View Rejected Proof
                        </button>
                      )}
                    </div>
                  )}
              </div>

              <div className="seller-form-grid-white seller-address-form-grid">
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">
                    Address Line 1
                  </label>
                  <div className="seller-input-icon-white">
                    <FaMapMarkerAlt />
                    <input
                      name="address_line1"
                      type="text"
                      value={businessAddressForm.address_line1}
                      onChange={handleBusinessAddressChange}
                      placeholder="Office, building, or street address"
                    />
                  </div>
                </div>
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">
                    Address Line 2
                  </label>
                  <div className="seller-input-icon-white">
                    <FaMapMarkerAlt />
                    <input
                      name="address_line2"
                      type="text"
                      value={businessAddressForm.address_line2}
                      onChange={handleBusinessAddressChange}
                      placeholder="Suite, floor, or landmark"
                    />
                  </div>
                </div>
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">Area</label>
                  <div className="seller-input-icon-white">
                    <FaMapMarkerAlt />
                    <input
                      name="area"
                      type="text"
                      value={businessAddressForm.area}
                      onChange={handleBusinessAddressChange}
                      placeholder="Area or district"
                    />
                  </div>
                </div>
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">City</label>
                  <div className="seller-input-icon-white">
                    <FaMapMarkerAlt />
                    <input
                      name="city"
                      type="text"
                      value={businessAddressForm.city}
                      onChange={handleBusinessAddressChange}
                      placeholder="City"
                    />
                  </div>
                </div>
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">Emirate</label>
                  <div className="seller-select-wrapper-white">
                    <select
                      name="emirate"
                      className="seller-form-select-white"
                      value={businessAddressForm.emirate}
                      onChange={handleBusinessAddressChange}
                    >
                      {UAE_EMIRATES.map((emirate) => (
                        <option key={emirate} value={emirate}>
                          {emirate}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">Country</label>
                  <div className="seller-input-icon-white">
                    <FaGlobe />
                    <input
                      name="country"
                      type="text"
                      value={businessAddressForm.country}
                      onChange={handleBusinessAddressChange}
                      placeholder="United Arab Emirates"
                    />
                  </div>
                </div>
                <div className="seller-form-group-white">
                  <label className="seller-form-label-white">Postal Code</label>
                  <div className="seller-input-icon-white">
                    <FaMapMarkerAlt />
                    <input
                      name="postal_code"
                      type="text"
                      value={businessAddressForm.postal_code}
                      onChange={handleBusinessAddressChange}
                      placeholder="Postal code"
                    />
                  </div>
                </div>
                <div className="seller-form-group-white seller-address-proof-group">
                  <label className="seller-form-label-white">
                    Business Address Proof
                  </label>
                  <div className="seller-address-proof-input">
                    <FaUpload />
                    <input
                      key={proofInputKey}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleProofFileChange}
                    />
                  </div>
                  <p className="seller-address-proof-help">
                    Upload a valid document showing your business address. PDF,
                    JPG, JPEG, and PNG are supported up to 5 MB.
                  </p>
                  {addressProofFile && (
                    <p className="seller-address-proof-selected">
                      Selected proof: {addressProofFile.name}
                    </p>
                  )}
                  {!addressProofFile && activeProofRecord?.proof_available && (
                    <button
                      type="button"
                      className="seller-address-proof-link"
                      onClick={() => openProofPreview(activeProofRecord.id)}
                      disabled={proofPreviewLoading}
                    >
                      View Latest Uploaded Proof
                    </button>
                  )}
                </div>
              </div>

              <div className="seller-address-actions">
                <button
                  type="button"
                  className="dashboard-export-btn"
                  onClick={handleSubmitBusinessAddress}
                  disabled={addressSubmitting}
                >
                  <FaUpload />
                  {addressSubmitting
                    ? "Submitting..."
                    : verificationStatus === "rejected" ||
                        approvedBusinessAddress
                      ? "Resubmit for Verification"
                      : "Submit for Verification"}
                </button>
              </div>
            </div>

            <div className="seller-action-buttons-white">
              <button
                className="dashboard-export-btn"
                onClick={handleSaveProfile}
              >
                <FaSave /> Save Changes
              </button>
              <button className="seller-button-alt-white">Cancel</button>
            </div>
          </div>
        );

      case "store":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">Store Information</h3>
              <p className="seller-section-description-white">
                Manage your store details and preferences
              </p>
            </div> */}

            <div className="seller-form-grid-white">
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Store Name *</label>
                <input
                  type="text"
                  className="seller-form-input-white"
                  value={storeData.storeName}
                  onChange={(e) =>
                    handleInputChange("storeName", e.target.value)
                  }
                />
              </div>
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Store URL</label>
                <div className="seller-input-icon-white">
                  <FaGlobe />
                  <input
                    type="text"
                    value={storeData.storeUrl}
                    onChange={(e) =>
                      handleInputChange("storeUrl", e.target.value)
                    }
                  />
                  <button className="seller-copy-button-white">
                    <FiCopy />
                  </button>
                </div>
              </div>
              {/* <div className="seller-form-group-white">
                <label className="seller-form-label-white">Currency</label>
                <select
                  className="seller-form-select-white"
                  value={storeData.currency}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="INR">INR (₹)</option>
                </select>
              </div> */}
              {/* <div className="seller-form-group-white">
                <label className="seller-form-label-white">Timezone</label>
                <div className="seller-input-icon-white">
                  <FaRegClock />
                  <select
                    value={storeData.timezone}
                    onChange={(e) =>
                      handleInputChange("timezone", e.target.value)
                    }
                  >
                    <option value="GMT-5">GMT-5 (Eastern Time)</option>
                    <option value="GMT">GMT (London)</option>
                    <option value="GMT+5.5">GMT+5.5 (IST)</option>
                    <option value="GMT+8">GMT+8 (Singapore)</option>
                  </select>
                </div>
              </div> */}
              <div className="seller-form-group-white seller-form-group-wide-white">
                <label className="seller-form-label-white">
                  Store Description
                </label>
                <textarea
                  className="seller-form-textarea-white"
                  value={storeData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="seller-stats-section-white">
              <h4 className="seller-stats-title-white">Store Statistics</h4>
              <div className="seller-stats-grid-white">
                <div className="seller-stat-card-white">
                  <div className="seller-stat-icon-white">
                    <FiPackage />
                  </div>
                  <div className="seller-stat-content-white">
                    <div className="seller-stat-value-white">156</div>
                    <div className="seller-stat-label-white">
                      Total Products
                    </div>
                  </div>
                </div>
                <div className="seller-stat-card-white">
                  <div className="seller-stat-icon-white">
                    <FaChartLine />
                  </div>
                  <div className="seller-stat-content-white">
                    <div className="seller-stat-value-white">2,845</div>
                    <div className="seller-stat-label-white">
                      Monthly Orders
                    </div>
                  </div>
                </div>
                <div className="seller-stat-card-white">
                  <div className="seller-stat-icon-white">
                    <FiStar />
                  </div>
                  <div className="seller-stat-content-white">
                    <div className="seller-stat-value-white">4.8</div>
                    <div className="seller-stat-label-white">Store Rating</div>
                  </div>
                </div>
                <div className="seller-stat-card-white">
                  <div className="seller-stat-icon-white">
                    <FaPercent />
                  </div>
                  <div className="seller-stat-content-white">
                    <div className="seller-stat-value-white seller-stat-value-success-white">
                      +12%
                    </div>
                    <div className="seller-stat-label-white">This Month</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "security":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">Security Settings</h3>
              <p className="seller-section-description-white">
                Manage your account security and privacy
              </p>
            </div> */}

            <div className="seller-security-section-white">
              <div className="seller-security-item-white">
                <div className="seller-security-info-white">
                  <div className="seller-security-icon-white">
                    <FaLock />
                  </div>
                  <div>
                    <h4 className="seller-security-title-white">
                      Two-Factor Authentication
                    </h4>
                    <p className="seller-security-desc-white">
                      Add an extra layer of security to your account
                    </p>
                  </div>
                </div>
                <label className="seller-toggle-wrapper-white">
                  <input
                    type="checkbox"
                    checked={twoFactorEnabled}
                    onChange={() => setTwoFactorEnabled(!twoFactorEnabled)}
                  />
                  <span className="seller-toggle-slider-white"></span>
                </label>
              </div>

              <div className="seller-password-section-white">
                <h4 className="seller-password-title-white">Change Password</h4>
                <div className="seller-password-form-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      Current Password
                    </label>
                    <div className="seller-password-input-white">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                      />
                      <button
                        className="seller-password-toggle-white"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      New Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className="seller-form-input-white"
                    />
                  </div>
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className="seller-form-input-white"
                    />
                  </div>
                  <button className="seller-button-main-white">
                    Update Password
                  </button>
                </div>
              </div>

              <div className="seller-sessions-section-white">
                <h4 className="seller-sessions-title-white">Active Sessions</h4>
                <div className="seller-sessions-list-white">
                  <div className="seller-session-item-white">
                    <div>
                      <div className="seller-session-title-white">
                        Current Session
                      </div>
                      <div className="seller-session-details-white">
                        Chrome on Windows • Just now
                      </div>
                    </div>
                    <span className="seller-session-status-white">Active</span>
                  </div>
                  <div className="seller-session-item-white">
                    <div>
                      <div className="seller-session-title-white">
                        Mobile Device
                      </div>
                      <div className="seller-session-details-white">
                        Safari on iPhone • 2 hours ago
                      </div>
                    </div>
                    <button className="seller-session-revoke-white">
                      <FaTrash /> Revoke
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "notifications":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">
                Notification Preferences
              </h3>
              <p className="seller-section-description-white">
                Choose what notifications you want to receive
              </p>
            </div> */}

            <div className="seller-notifications-list-white">
              {Object.entries(notifications).map(([key, value]) => (
                <div key={key} className="seller-notification-item-white">
                  <div>
                    <h4 className="seller-notification-title-white">
                      {key.replace(/([A-Z])/g, " $1")}
                    </h4>
                    <p className="seller-notification-desc-white">
                      {key === "email" && "Receive email notifications"}
                      {key === "sms" && "Receive SMS alerts"}
                      {key === "orderUpdates" &&
                        "Get notified about new orders"}
                      {key === "promotions" && "Receive promotional offers"}
                      {key === "lowStock" && "Get alerts for low stock items"}
                      {key === "newReviews" &&
                        "Notify when new reviews are posted"}
                      {key === "marketingTips" &&
                        "Receive marketing suggestions"}
                    </p>
                  </div>
                  <label className="seller-toggle-wrapper-white">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={() => handleNotificationToggle(key)}
                    />
                    <span className="seller-toggle-slider-white"></span>
                  </label>
                </div>
              ))}
            </div>

            <div className="seller-frequency-section-white">
              <h4 className="seller-frequency-title-white">
                Notification Frequency
              </h4>
              <div className="seller-frequency-options-white">
                <button className="seller-frequency-option-white">
                  <div className="seller-frequency-name-white">Instant</div>
                  <div className="seller-frequency-desc-white">
                    Real-time alerts
                  </div>
                </button>
                <button className="seller-frequency-option-white active-white">
                  <div className="seller-frequency-name-white">
                    Daily Digest
                  </div>
                  <div className="seller-frequency-desc-white">
                    Once per day
                  </div>
                </button>
                <button className="seller-frequency-option-white">
                  <div className="seller-frequency-name-white">
                    Weekly Summary
                  </div>
                  <div className="seller-frequency-desc-white">
                    Weekly reports
                  </div>
                </button>
              </div>
            </div>
          </div>
        );
      case "payment":
        return (
          <div className="seller-profile-section-white">
            {/* Bank Details Section */}
            <div className="seller-bank-details-section-white">
              <h3 className="seller-section-subtitle-white">Bank Account Details</h3>
              <p className="seller-section-description-white">
                Add your bank account details to receive payouts
              </p>

              <div className="seller-bank-form-white">
                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      Account Holder Name <span className="seller-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter account holder name"
                      className="seller-form-input-white"
                    />
                  </div>
                </div>

                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      Bank Name <span className="seller-required-star">*</span>
                    </label>
                    <select className="seller-form-select-white">
                      <option value="">Select Bank</option>
                      <option value="emirates_nbd">Emirates NBD</option>
                      <option value="abu_dhabi_islamic">Abu Dhabi Islamic Bank</option>
                      <option value="mashreq_bank">Mashreq Bank</option>
                      <option value="dubai_islamic_bank">Dubai Islamic Bank</option>
                      <option value="rakbank">RAKBANK</option>
                      <option value="adcb">ADCB</option>
                      <option value="fgb">First Gulf Bank</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      Account Number <span className="seller-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter account number"
                      className="seller-form-input-white"
                    />
                  </div>
                </div>

                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      Confirm Account Number <span className="seller-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Re-enter account number"
                      className="seller-form-input-white"
                    />
                  </div>
                </div>

                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">
                      IFSC Code / SWIFT Code <span className="seller-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter IFSC or SWIFT code"
                      className="seller-form-input-white"
                    />
                  </div>
                </div>

                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">IBAN (Optional)</label>
                    <input
                      type="text"
                      placeholder="Enter IBAN number"
                      className="seller-form-input-white"
                    />
                  </div>
                </div>

                <div className="seller-form-row-white">
                  <div className="seller-form-group-white">
                    <label className="seller-form-label-white">Branch Address (Optional)</label>
                    <input
                      type="text"
                      placeholder="Enter branch address"
                      className="seller-form-input-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* KYC Details Section */}
            <div className="seller-kyc-section-white">
              <h3 className="seller-section-subtitle-white">KYC Verification</h3>
              <p className="seller-section-description-white">
                Upload your documents for verification (Required for payouts)
              </p>

              <div className="seller-kyc-status-white">
                <div className="seller-kyc-status-icon-white">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4CAF50" />
                  </svg>
                </div>
                <div className="seller-kyc-status-text-white">
                  <span className="seller-kyc-status-title-white">Verification Status:</span>
                  <span className="seller-kyc-status-badge-white seller-kyc-status-pending-white">Pending</span>
                </div>
              </div>

              <div className="seller-kyc-documents-grid-white">
                {/* Identity Proof */}
                <div className="seller-kyc-document-card-white">
                  <div className="seller-kyc-document-header-white">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 2h6v2h-6V6zm0 4h6v2h-6v-2zm-6 0h4v2H6v-2zm10 4h-4v-2h4v2zm-10 0h4v2H6v-2z" fill="#2a568f" />
                    </svg>
                    <h4 className="seller-kyc-document-title-white">Identity Proof</h4>
                  </div>
                  <p className="seller-kyc-document-desc-white">
                    Passport, Driver's License, or National ID
                  </p>
                  <div className="seller-kyc-upload-area-white">
                    <input type="file" id="id-proof" className="seller-kyc-file-input-white" />
                    <label htmlFor="id-proof" className="seller-kyc-upload-label-white">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
                      </svg>
                      Upload Document
                    </label>
                    <span className="seller-kyc-file-name-white">No file chosen</span>
                  </div>
                </div>

                {/* Address Proof */}
                <div className="seller-kyc-document-card-white">
                  <div className="seller-kyc-document-header-white">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#2a568f" />
                    </svg>
                    <h4 className="seller-kyc-document-title-white">Address Proof</h4>
                  </div>
                  <p className="seller-kyc-document-desc-white">
                    Utility Bill, Bank Statement, or Rent Agreement
                  </p>
                  <div className="seller-kyc-upload-area-white">
                    <input type="file" id="address-proof" className="seller-kyc-file-input-white" />
                    <label htmlFor="address-proof" className="seller-kyc-upload-label-white">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
                      </svg>
                      Upload Document
                    </label>
                    <span className="seller-kyc-file-name-white">No file chosen</span>
                  </div>
                </div>

                {/* Tax Information */}
                <div className="seller-kyc-document-card-white">
                  <div className="seller-kyc-document-header-white">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 2h6v2h-6V6zm0 4h6v2h-6v-2zm-6 0h4v2H6v-2zm10 4h-4v-2h4v2zm-10 0h4v2H6v-2z" fill="#2a568f" />
                    </svg>
                    <h4 className="seller-kyc-document-title-white">Tax Information</h4>
                  </div>
                  <p className="seller-kyc-document-desc-white">
                    VAT Certificate, Tax Registration, or TRN
                  </p>
                  <div className="seller-kyc-upload-area-white">
                    <input type="file" id="tax-proof" className="seller-kyc-file-input-white" />
                    <label htmlFor="tax-proof" className="seller-kyc-upload-label-white">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
                      </svg>
                      Upload Document
                    </label>
                    <span className="seller-kyc-file-name-white">No file chosen</span>
                  </div>
                </div>

                {/* Trade License (Optional) */}
                <div className="seller-kyc-document-card-white">
                  <div className="seller-kyc-document-header-white">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm10 16H4V8h16v12z" fill="#2a568f" />
                    </svg>
                    <h4 className="seller-kyc-document-title-white">Trade License</h4>
                  </div>
                  <p className="seller-kyc-document-desc-white">
                    Business Trade License (Optional for individual sellers)
                  </p>
                  <div className="seller-kyc-upload-area-white">
                    <input type="file" id="trade-license" className="seller-kyc-file-input-white" />
                    <label htmlFor="trade-license" className="seller-kyc-upload-label-white">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
                      </svg>
                      Upload Document
                    </label>
                    <span className="seller-kyc-file-name-white">No file chosen</span>
                  </div>
                </div>
              </div>

              {/* KYC Guidelines */}
              <div className="seller-kyc-guidelines-white">
                <h4 className="seller-kyc-guidelines-title-white">Document Guidelines:</h4>
                <ul className="seller-kyc-guidelines-list-white">
                  <li>File format: PDF, JPG, or PNG (Max size: 5MB)</li>
                  <li>Documents must be clear and legible</li>
                  <li>All documents should be valid and not expired</li>
                  <li>Name on documents should match your registered business name</li>
                  <li>Verification typically takes 24-48 hours</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="seller-kyc-actions-white">
                <button className="seller-kyc-save-draft-btn-white">
                  Save as Draft
                </button>
                <button className="seller-kyc-submit-btn-white">
                  Submit for Verification
                </button>
              </div>
            </div>
          </div>
        );
      case "shipping":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">Shipping Settings</h3>
              <p className="seller-section-description-white">
                Configure your shipping methods and rates
              </p>
            </div> */}

            <div className="seller-form-grid-white">
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">
                  Processing Time
                </label>
                <select className="seller-form-select-white">
                  <option>Same Day</option>
                  <option>1-2 Business Days</option>
                  <option>3-5 Business Days</option>
                  <option>5-7 Business Days</option>
                </select>
              </div>
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">
                  Shipping Origin
                </label>
                <div className="seller-input-icon-white">
                  <FaMapMarkerAlt />
                  <input
                    type="text"
                    className="seller-form-input-white"
                    placeholder="Enter shipping origin"
                  />
                </div>
              </div>
              <div className="seller-form-group-white">
                <label className="seller-form-label-white">
                  Free Shipping Threshold
                </label>
                <div className="seller-currency-input-white">
                  <span className="seller-currency-symbol-white">AED</span>
                  <input
                    type="number"
                    placeholder="50"
                    className="seller-form-input-white"
                    style={{
                      borderLeft: "none",
                      borderRadius: "0 12px 12px 0",
                    }}
                  />
                </div>
              </div>
              {/* <div className="seller-form-group-white">
                <label className="seller-form-label-white">
                  Shipping Provider
                </label>
                <select className="seller-form-select-white">
                  <option>Select Provider</option>
                  <option>FedEx</option>
                  <option>UPS</option>
                  <option>USPS</option>
                  <option>DHL</option>
                </select>
              </div> */}
            </div>

            <div className="seller-action-buttons-white">
              <button className="seller-button-main-white">
                <FaSave /> Save Shipping Settings
              </button>
            </div>
          </div>
        );

      case "appearance":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">Store Appearance</h3>
              <p className="seller-section-description-white">
                Customize how your store looks to customers
              </p>
            </div> */}

            <div className="seller-form-grid-white">
              <div className="seller-form-group-white seller-form-group-wide-white">
                <label className="seller-form-label-white">
                  Store Theme Color
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "25px",
                    alignItems: "center",
                    marginTop: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, var(--bold-blue-cta), var(--sky-blue))",
                      border: "4px solid var(--pure-white)",
                      boxShadow: "0 8px 25px rgba(37, 99, 235, 0.2)",
                    }}
                  ></div>
                  <button className="seller-button-main-white">
                    <FaPalette /> Change Theme
                  </button>
                </div>
              </div>

              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Store Logo</label>
                <div
                  style={{
                    padding: "30px",
                    border: "3px dashed var(--sky-blue)",
                    borderRadius: "16px",
                    textAlign: "center",
                    background: "var(--ice-blue)",
                    marginTop: "10px",
                  }}
                >
                  <FaUpload
                    style={{
                      fontSize: "2.5rem",
                      marginBottom: "15px",
                      color: "var(--bold-blue-cta)",
                    }}
                  />
                  <p
                    style={{
                      color: "var(--deep-navy)",
                      marginBottom: "15px",
                      fontWeight: "500",
                    }}
                  >
                    Upload your store logo
                  </p>
                  <button className="seller-button-alt-white">
                    Choose File
                  </button>
                </div>
              </div>

              <div className="seller-form-group-white">
                <label className="seller-form-label-white">Banner Image</label>
                <div
                  style={{
                    padding: "30px",
                    border: "3px dashed var(--sky-blue)",
                    borderRadius: "16px",
                    textAlign: "center",
                    background: "var(--ice-blue)",
                    marginTop: "10px",
                  }}
                >
                  <FaUpload
                    style={{
                      fontSize: "2.5rem",
                      marginBottom: "15px",
                      color: "var(--bold-blue-cta)",
                    }}
                  />
                  <p
                    style={{
                      color: "var(--deep-navy)",
                      marginBottom: "15px",
                      fontWeight: "500",
                    }}
                  >
                    Upload store banner
                  </p>
                  <button className="seller-button-alt-white">
                    Choose File
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case "integration":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">Integrations</h3>
              <p className="seller-section-description-white">
                Connect with third-party services
              </p>
            </div> */}

            <div className="seller-stats-grid-white">
              {integrations.map((integration, index) => (
                <div key={index} className="seller-stat-card-white">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                      marginBottom: "20px",
                    }}
                  >
                    <span style={{ fontSize: "2rem" }}>{integration.icon}</span>
                    <h4
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "var(--deep-navy)",
                      }}
                    >
                      {integration.name}
                    </h4>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "20px",
                    }}
                  >
                    <span
                      className={
                        integration.connected
                          ? "seller-status-badge-connected-white"
                          : "seller-status-badge-disconnected-white"
                      }
                      style={{
                        padding: "8px 20px",
                        borderRadius: "20px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                    >
                      {integration.connected ? "Connected" : "Connect"}
                    </span>
                    {/* <button
                      className="seller-button-alt-white"
                      style={{ padding: "10px 20px", fontSize: "1rem" }}
                    >
                      {integration.connected ? "Manage" : "Connect"}
                      <FaChevronRight style={{ marginLeft: "8px" }} />
                    </button> */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "documents":
        return (
          <div className="seller-profile-section-white">
            {/* <div className="seller-section-header-white">
              <h3 className="seller-section-title-white">Documents & Legal</h3>
              <p className="seller-section-description-white">
                Manage your store's legal documents
              </p>
            </div> */}

            <div className="seller-form-grid-white">
              <div className="seller-form-group-white seller-form-group-wide-white">
                <label className="seller-form-label-white">
                  Terms & Conditions
                </label>
                <textarea
                  className="seller-form-textarea-white"
                  placeholder="Enter your store's terms and conditions..."
                  rows={6}
                />
              </div>
              <div className="seller-form-group-white seller-form-group-wide-white">
                <label className="seller-form-label-white">
                  Privacy Policy
                </label>
                <textarea
                  className="seller-form-textarea-white"
                  placeholder="Enter your store's privacy policy..."
                  rows={6}
                />
              </div>
              <div className="seller-form-group-white seller-form-group-wide-white">
                <label className="seller-form-label-white">Return Policy</label>
                <textarea
                  className="seller-form-textarea-white"
                  placeholder="Enter your store's return policy..."
                  rows={6}
                />
              </div>
            </div>

            <div className="seller-action-buttons-white">
              <button className="seller-button-main-white">
                <FaSave /> Save Documents
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="seller-empty-state-white">
            <div className="seller-empty-icon-white">
              <FiSettings />
            </div>
            <h3 className="seller-empty-title-white">
              Select a Setting Category
            </h3>
            <p className="seller-empty-desc-white">
              Choose from the sidebar to manage your settings
            </p>
          </div>
        );
    }
  };

  return (
    <div className="seller-settings-white">
      <div className="seller-settings-container-white">
        {/* Header */}
        {/* <div className="seller-settings-title-white">
          <h1>Seller Settings</h1>
          <p className="seller-settings-subtitle-white">
            Manage your store preferences and account settings
          </p>
        </div> */}

        <div className="seller-settings-grid-white">
          {/* Sidebar */}
          <div className="seller-settings-sidebar-white">
            {/* <div className="seller-sidebar-profile-white">
              <div className="seller-sidebar-avatar-white">
                <FaUser />
              </div>
              <div className="seller-sidebar-info-white">
                <div className="seller-sidebar-name-white">John Doe</div>
                <div className="seller-sidebar-badge-white">Premium Seller</div>
              </div>
            </div> */}

            <nav className="seller-nav-menu-white">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`seller-nav-button-white ${activeTab === item.id ? "active-nav-white" : ""
                    }`}
                >
                  <span className="seller-nav-icon-white">{item.icon}</span>
                  <span className="seller-nav-text-white">{item.label}</span>
                  {activeTab === item.id && (
                    <FaCheck className="seller-nav-indicator-white" />
                  )}
                </button>
              ))}
            </nav>

            <div className="seller-sidebar-actions-white">
              <button
                className="dashboard-export-btn"
                onClick={handleSaveProfile}
              >
                <FaSave /> Save All Changes
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="seller-settings-main-white">
            <div className="seller-settings-content-white">
              <div className="seller-content-header-white">
                <div className="seller-content-title-white">
                  <h2>
                    {menuItems.find((item) => item.id === activeTab)?.label ||
                      ""}
                  </h2>
                  <p className="seller-content-subtitle-white">
                    {activeTab === "store" &&
                      "Manage your store settings and preferences"}
                    {activeTab === "security" &&
                      "Secure your account and manage access"}
                    {activeTab === "notifications" &&
                      "Control your notification preferences"}
                    {activeTab === "payment" &&
                      "Manage payment methods and payouts"}
                    {activeTab === "shipping" &&
                      "Configure shipping methods and rates"}
                    {activeTab === "appearance" &&
                      "Customize your store appearance"}
                    {activeTab === "integration" &&
                      "Connect with third-party services"}
                    {activeTab === "documents" &&
                      "Manage legal documents and policies"}
                  </p>
                </div>
                {/* <div className="seller-last-updated-white">
                  Last updated: Today
                </div> */}
              </div>

              {renderContent()}
            </div>

            {/* Quick Stats */}
            <div className="seller-quick-stats-white">
              <div className="seller-quick-stat-card-white">
                <div className="seller-quick-stat-header-white">
                  <div className="seller-quick-stat-icon-white">
                    <FiPackage />
                  </div>
                  <div>
                    <div className="seller-quick-stat-title-white">
                      Active Products
                    </div>
                    <div className="seller-quick-stat-value-white">
                      {stats.activeProducts}
                    </div>
                  </div>
                </div>
              </div>
              <div className="seller-quick-stat-card-white">
                <div className="seller-quick-stat-header-white">
                  <div className="seller-quick-stat-icon-white">
                    AED
                  </div>
                  <div>
                    <div className="seller-quick-stat-title-white">
                      This Month Sales
                    </div>
                    <div className="seller-quick-stat-value-white">
                      {stats.monthlySales}
                    </div>
                  </div>
                </div>
              </div>
              <div className="seller-quick-stat-card-white">
                <div className="seller-quick-stat-header-white">
                  <div className="seller-quick-stat-icon-white">
                    <FaShippingFast />
                  </div>
                  <div>
                    <div className="seller-quick-stat-title-white">
                      Pending Orders
                    </div>
                    <div className="seller-quick-stat-value-white">
                      {stats.pendingOrders}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SellerSetting;
